import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Sun, CalendarClock, UserX, Bell, Loader2, ChevronRight, MessageCircle, Phone, Sparkles, FileSignature, CheckCircle2, Clock, RefreshCw, FilePen } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BUCKET_META = {
  overdue:   { label: "Da ricontattare",  icon: AlertTriangle, cls: "border-red-600 bg-red-50 text-red-700" },
  today:     { label: "Oggi",             icon: Bell,          cls: "border-[#FF5A1F] bg-orange-50 text-[#FF5A1F]" },
  contacted: { label: "Già contattato",   icon: CheckCircle2,  cls: "border-emerald-600 bg-emerald-50 text-emerald-700" },
  no_show:   { label: "No show",          icon: UserX,         cls: "border-red-500 bg-red-50 text-red-600" },
  future:    { label: "In programma",     icon: CalendarClock, cls: "border-blue-600 bg-blue-50 text-blue-700" },
  other:     { label: "Altro",            icon: Bell,          cls: "border-[#0A0A0A]/40 bg-[#F2F2EC] text-[#0A0A0A]" },
};

export default function AdminDashboardOperatore() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [syncing, setSyncing] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null); // null | { updates, appends, ... }

  const openSyncPreview = async () => {
    setSyncing(true);
    try {
      const res = await api.get("/mio-giorno/sync-sheet/preview");
      setSyncPreview(res.data);
    } catch (e) {
      toast.error("Errore anteprima");
    } finally {
      setSyncing(false);
    }
  };

  const executeSyncSheet = async (onlyUpdates) => {
    setSyncing(true);
    try {
      const res = await api.post(`/mio-giorno/sync-sheet?only_updates=${onlyUpdates}`);
      const d = res.data || {};
      toast.success(
        `Sync completato: ${d.updated_meta || 0} Meta aggiornati, ${d.appended_crm || 0} CRM aggiunti` +
        (d.errors ? ` (${d.errors} errori)` : "")
      );
      setSyncPreview(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore sincronizzazione");
    } finally {
      setSyncing(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Usa lo STESSO range calendar del Report KPI (localStorage condiviso),
      // così il tasso conversione in Home e i Lead Unici del Report usano la stessa base.
      let start, end;
      try {
        const raw = localStorage.getItem("perla.reportKpi.calendar");
        if (raw) {
          const stored = JSON.parse(raw);
          start = stored.calStart;
          end = stored.calEnd;
        }
      } catch { /* ignore */ }
      // Fallback: lo stesso default del Report KPI (2026-08-01 → oggi)
      if (!start) start = "2026-08-01";
      if (!end) end = new Date().toISOString().slice(0, 10);
      const [r1, r2] = await Promise.all([
        api.get("/mio-giorno/dashboard-reminders", { params: { start, end } }),
        api.get("/mio-giorno/reports"),
      ]);
      setData(r1.data);
      setReports(r2.data);
    } catch (e) { toast.error("Errore caricamento dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCard = (r) => {
    navigate("/admin/mio-giorno", { state: { scroll_to_card: { date: r.card_date, cardId: r.card_id } } });
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("mg-jump-to-card", { detail: { date: r.card_date, cardId: r.card_id } }));
    }, 150);
  };

  const openContract = (id) => {
    navigate("/admin/contratti", { state: { open_contract_id: id } });
  };

  const firstName = (user?.name || "").split(" ")[0];
  const filtered = data?.reminders?.filter((r) => filter === "all" || r.bucket === filter) || [];

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-10" data-testid="admin-dashboard-operatore">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
          <Sparkles size={14} /> Home
        </div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">
          {greetingByHour()}, {firstName || "collega"}
        </h1>
        <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-xl">
          Focus del giorno: lead scoperti (rossi), lead ancora <b>da contattare</b>, e quelli in <b>attesa firma</b>.
        </p>
        <div className="mt-4">
          <button
            data-testid="btn-sync-sheet-vendor"
            onClick={openSyncPreview}
            disabled={syncing}
            className="inline-flex items-center gap-2 rounded-xl border border-[#0A0A0A]/20 bg-white px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#0A0A0A] hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition disabled:opacity-50 disabled:cursor-not-allowed"
            title="Mostra ANTEPRIMA di cosa verrà scritto sul foglio Google (nessuna scrittura immediata)"
          >
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {syncing ? "Sincronizzazione…" : "Anteprima sync sul foglio Google"}
          </button>
        </div>
      </header>

      {syncPreview && (
        <SyncPreviewModal
          preview={syncPreview}
          onClose={() => setSyncPreview(null)}
          onConfirm={executeSyncSheet}
          busy={syncing}
        />
      )}

      {/* KPI counts */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <BucketCard active={filter === "overdue"} onClick={() => setFilter("overdue")} label="Da ricontattare" count={data.counts.overdue} bucket="overdue" />
          <BucketCard active={filter === "today"} onClick={() => setFilter("today")} label="Oggi" count={data.counts.today} bucket="today" />
          <BucketCard active={filter === "contacted"} onClick={() => setFilter("contacted")} label="Già contattati" count={data.counts.contacted} bucket="contacted" />
          <ConversionCard
            rate={data.conversion?.rate ?? 0}
            rateEff={data.conversion?.rate_effective ?? 0}
            signed={data.conversion?.signed ?? 0}
            total={data.conversion?.total_leads ?? 0}
            noShow={data.conversion?.no_show ?? 0}
          />
        </div>
      )}

      {loading && !data && (
        <div className="text-sm text-[#0A0A0A]/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carico...</div>
      )}

      {/* Reports: No-show + In attesa firma + Riprogrammati + Bozze */}
      {reports && (reports.no_show.total > 0 || reports.pending_signature.total > 0 || reports.rescheduled.total > 0 || (reports.drafts?.total || 0) > 0) && (
        <div className="mb-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(reports.drafts?.total || 0) > 0 && (
            <div className="border-2 border-yellow-500 bg-yellow-50 p-4" data-testid="report-drafts">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-yellow-700 mb-2">
                <FilePen size={14} /> Bozze contratto ({reports.drafts.total})
              </div>
              <div className="text-[11px] text-yellow-700/70 mb-3">Contratti creati ma NON ancora inviati. Completali o inviali per non perderli.</div>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {reports.drafts.items.map((d) => (
                  <div
                    key={d.contract_id}
                    className="w-full flex items-center justify-between gap-3 p-2 bg-white border-2 border-yellow-200 hover:border-yellow-500 group"
                    data-testid={`draft-${d.contract_id}`}
                  >
                    <button
                      onClick={() => openContract(d.contract_id)}
                      className="text-left flex-1 min-w-0"
                    >
                      <div className="font-display text-sm uppercase text-[#0A0A0A] truncate">{d.artist_name || "Senza nome"}</div>
                      <div className="text-[10px] text-[#0A0A0A]/60 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><Clock size={10}/>
                          {d.days_since_update === 0 ? "oggi" : d.days_since_update === 1 ? "ieri" : `${d.days_since_update} giorni fa`}
                        </span>
                        {d.importo_totale && <span>€ {d.importo_totale}</span>}
                      </div>
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await api.post(`/contratti/${d.contract_id}/mark-sent`, { channel: "whatsapp" });
                          toast.success("Contratto marcato come inviato");
                          await load();
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Errore");
                        }
                      }}
                      className="shrink-0 px-2 py-1 border border-yellow-600 text-yellow-700 hover:bg-yellow-600 hover:text-white text-[9px] uppercase tracking-widest font-bold"
                      title="Segna come inviato — sposterà il contratto in 'attesa firma'"
                      data-testid={`draft-mark-sent-${d.contract_id}`}
                    >
                      Segna inviato
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {reports.pending_signature.total > 0 && (
            <div className="border-2 border-amber-500 bg-amber-50 p-4" data-testid="report-pending-signature">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-700 mb-2">
                <FileSignature size={14} /> Contratti in attesa firma ({reports.pending_signature.total})
              </div>
              <div className="text-[11px] text-amber-700/70 mb-3">Inviati ma non ancora firmati. Un sollecito potrebbe sbloccare.</div>
              <div className="space-y-2">
                {reports.pending_signature.items.map((p) => (
                  <button
                    key={p.contract_id}
                    onClick={() => openContract(p.contract_id)}
                    className="w-full text-left flex items-center justify-between gap-3 p-2 bg-white border-2 border-amber-200 hover:border-amber-500 group"
                    data-testid={`pending-${p.contract_id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display text-sm uppercase text-[#0A0A0A] truncate">{p.artist_name}</div>
                      <div className="text-[10px] text-[#0A0A0A]/60 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><Clock size={10}/> Inviato {p.days_since_sent === 0 ? "oggi" : p.days_since_sent === 1 ? "ieri" : `${p.days_since_sent} giorni fa`}</span>
                        {p.importo_totale && <span>€ {p.importo_totale}</span>}
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-amber-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {reports.no_show.total > 0 && (
            <div className="border-2 border-red-500 bg-red-50 p-4" data-testid="report-no-show">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-red-700 mb-2">
                <UserX size={14} /> No show ({reports.no_show.total})
              </div>
              <div className="text-[11px] text-red-700/70 mb-3">Non si sono presentati alla call — da ricontattare o chiudere.</div>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {reports.no_show.items.map((n) => (
                  <button
                    key={n.card_id}
                    onClick={() => openCard({ card_id: n.card_id, card_date: n.card_date })}
                    className="w-full text-left flex items-center justify-between gap-3 p-2 bg-white border-2 border-red-200 hover:border-red-500 group"
                    data-testid={`noshow-${n.card_id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display text-sm uppercase text-[#0A0A0A] truncate">{n.artist_name}</div>
                      <div className="text-[10px] text-[#0A0A0A]/60 flex items-center gap-1 flex-wrap">
                        <Clock size={10}/> {n.days_ago === 0 ? "oggi" : n.days_ago === 1 ? "ieri" : `${n.days_ago} giorni fa`}
                        {n.no_show_count > 1 && (
                          <span className="ml-1 px-1.5 py-0.5 bg-red-600 text-white font-bold text-[9px] uppercase tracking-wider">
                            {n.no_show_count}× no-show
                          </span>
                        )}
                        {n.reschedule_count > 0 && <span className="ml-2">⟳ {n.reschedule_count} riprogr.</span>}
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-red-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {reports.rescheduled.total > 0 && (
            <div className="border-2 border-blue-500 bg-blue-50 p-4" data-testid="report-rescheduled">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-700 mb-2">
                <RefreshCw size={14} /> Appuntamenti riprogrammati ({reports.rescheduled.total})
              </div>
              <div className="text-[11px] text-blue-700/70 mb-3">Lead che hanno cambiato appuntamento — verifica se serve un contatto.</div>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {reports.rescheduled.items.map((r) => (
                  <button
                    key={r.card_id}
                    onClick={() => openCard({ card_id: r.card_id, card_date: r.next_scheduled_date || r.card_date })}
                    className="w-full text-left flex items-center justify-between gap-3 p-2 bg-white border-2 border-blue-200 hover:border-blue-500 group"
                    data-testid={`resch-${r.card_id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-display text-sm uppercase text-[#0A0A0A] truncate">{r.artist_name}</div>
                      <div className="text-[10px] text-[#0A0A0A]/60 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1"><RefreshCw size={10}/> {r.reschedule_count}× riprogr.</span>
                        {r.next_scheduled_date && (
                          <span>→ {new Date(r.next_scheduled_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</span>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-blue-600 shrink-0 group-hover:translate-x-1 transition-transform" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {data && filtered.length === 0 && (
        <div className="border-2 border-dashed border-green-500 p-10 text-center bg-green-50" data-testid="empty-reminders">
          <Sparkles size={32} className="mx-auto mb-3 text-green-600" />
          <div className="font-display text-2xl uppercase text-green-700 mb-2">Zero pendenze</div>
          <div className="text-sm text-[#0A0A0A]/60">
            {filter === "all" ? "Nessun lead da ricontattare. Buon lavoro!" : "Nessun lead in questa categoria."}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((r) => {
          const bMeta = BUCKET_META[r.bucket] || BUCKET_META.other;
          const Icon = bMeta.icon;
          const dueLabel = r.days_diff < 0
            ? `${Math.abs(r.days_diff)} giorn${Math.abs(r.days_diff) === 1 ? "o" : "i"} fa`
            : r.days_diff === 0 ? "oggi" : `tra ${r.days_diff} giorn${r.days_diff === 1 ? "o" : "i"}`;
          const dueDateFmt = new Date(r.due_date).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" });
          return (
            <button
              key={`${r.card_id}-${r.bucket}`}
              onClick={() => openCard(r)}
              className={`w-full text-left p-4 border-2 ${bMeta.cls} hover:border-[#0A0A0A] transition-colors group flex items-start gap-3`}
              data-testid={`reminder-${r.card_id}`}
            >
              <Icon size={20} className="shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display text-lg uppercase text-[#0A0A0A]">{r.artist_name}</div>
                  <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border-2 ${bMeta.cls}`}>
                    {bMeta.label} · {r.bucket === "contacted" ? r.interaction_label : dueLabel}
                  </span>
                  {r.reschedule_count > 0 && (
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-blue-400 bg-white text-blue-600">
                      ⟳ {r.reschedule_count} rispostat{r.reschedule_count === 1 ? "a" : "e"}
                    </span>
                  )}
                  {r.bucket !== "contacted" && r.interaction_label && (
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-400 bg-white text-emerald-700 flex items-center gap-1">
                      {r.last_interaction_channel === "wa" ? <MessageCircle size={9}/> : <span>💬</span>}
                      {r.interaction_label}
                    </span>
                  )}
                </div>
                <div className="text-sm text-[#0A0A0A]/80 mt-1 leading-snug">{r.motivo}</div>
                <div className="flex items-center gap-3 mt-2 text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">
                  <span className="flex items-center gap-1"><CalendarClock size={10} /> {dueDateFmt}{r.due_time ? ` alle ${r.due_time}` : ""}</span>
                  {r.notes_count > 0 && <span>💬 {r.notes_count} not{r.notes_count === 1 ? "a" : "e"}</span>}
                  {r.phone && <span className="flex items-center gap-1"><Phone size={10} /> {r.phone}</span>}
                </div>
              </div>
              <div className="text-[#0A0A0A]/40 group-hover:text-[#FF5A1F] group-hover:translate-x-1 transition-all mt-2 flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold">
                Apri scheda <ChevronRight size={14} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BucketCard({ label, count, bucket, active, onClick }) {
  const meta = BUCKET_META[bucket] || BUCKET_META.other;
  const Icon = meta.icon;
  return (
    <button
      onClick={onClick}
      className={`p-4 border-2 text-left transition-colors ${active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : `${meta.cls} hover:border-[#0A0A0A]`}`}
      data-testid={`bucket-${bucket}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
        <Icon size={12} /> {label}
      </div>
      <div className="font-display text-3xl mt-1">{count}</div>
    </button>
  );
}

function ConversionCard({ rate, rateEff, signed, total, noShow }) {
  const fmt = (v) => (Number.isFinite(v) ? v.toFixed(1).replace(/\.0$/, "") : "0");
  const effectiveBase = Math.max(0, (total || 0) - (noShow || 0));
  return (
    <div
      className="p-4 border-2 border-[#FF5A1F] bg-orange-50 text-[#FF5A1F] text-left"
      data-testid="bucket-conversion"
      title={`${signed} contratti firmati · ${total} lead totali · ${noShow} no show`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
        <Sparkles size={12} /> Tasso conversione
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <div>
          <div className="font-display text-3xl leading-none">{fmt(rate)}%</div>
          <div className="text-[9px] uppercase tracking-widest text-[#FF5A1F]/70 mt-1">
            sui {total} lead
          </div>
          <div className="text-[9px] text-[#FF5A1F]/60">
            {signed} / {total}
          </div>
        </div>
        <div className="border-l-2 border-[#FF5A1F]/30 pl-3">
          <div className="font-display text-3xl leading-none">{fmt(rateEff)}%</div>
          <div className="text-[9px] uppercase tracking-widest text-[#FF5A1F]/70 mt-1">
            sulle call effettive
          </div>
          <div className="text-[9px] text-[#FF5A1F]/60">
            {signed} / {effectiveBase} <span className="opacity-60">({noShow} no show esclusi)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 12) return "Buongiorno";
  if (h < 18) return "Buon pomeriggio";
  return "Buonasera";
}

function SyncPreviewModal({ preview, onClose, onConfirm, busy }) {
  const { updates = [], appends = [], updates_count = 0, appends_count = 0, columns_touched = [] } = preview || {};
  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#FAFAF7] border-4 border-[#0A0A0A] max-w-5xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/60">Anteprima scrittura foglio Google</div>
            <div className="font-display text-2xl uppercase">Cosa verrà scritto</div>
          </div>
          <button onClick={onClose} className="text-[#FAFAF7]/60 hover:text-[#FF5A1F] text-xl">✕</button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 border-2 border-emerald-600 bg-emerald-50">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-800">
                Aggiornamenti sulle righe Meta esistenti (sicuro)
              </div>
              <div className="font-display text-3xl text-emerald-700 mt-1">{updates_count}</div>
            </div>
            <div className="p-3 border-2 border-amber-600 bg-amber-50">
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-800">
                Nuove righe "CRM:" da appendere (attenzione)
              </div>
              <div className="font-display text-3xl text-amber-700 mt-1">{appends_count}</div>
            </div>
          </div>
          <div className="text-xs text-[#0A0A0A]/60">
            Colonne toccate: <b>{columns_touched.join(" · ")}</b>. Nessuna altra colonna del foglio viene modificata.
          </div>

          {updates_count > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-2">
                Righe esistenti da aggiornare ({updates_count})
              </div>
              <div className="max-h-56 overflow-y-auto border-2 border-[#0A0A0A]">
                <table className="w-full text-xs">
                  <thead className="bg-[#F2F2EC] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1 border-b border-[#0A0A0A]/20">Artista</th>
                      <th className="text-left px-2 py-1 border-b border-[#0A0A0A]/20">Status</th>
                      <th className="text-left px-2 py-1 border-b border-[#0A0A0A]/20">Cosa scrivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {updates.map((u) => (
                      <tr key={u.card_id} className="border-b border-[#0A0A0A]/10">
                        <td className="px-2 py-1 font-semibold">{u.artist_name || "—"}</td>
                        <td className="px-2 py-1">{u.status || "—"}</td>
                        <td className="px-2 py-1 text-[10px] text-[#0A0A0A]/60">
                          {Object.entries(u.fields_to_write || {}).map(([k, v]) => `${k}=${String(v).slice(0, 40)}`).join(" · ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {appends_count > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-2">
                Nuove righe che verranno aggiunte ({appends_count})
              </div>
              <div className="max-h-56 overflow-y-auto border-2 border-amber-600">
                <table className="w-full text-xs">
                  <thead className="bg-amber-50 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1 border-b border-amber-300">Artista</th>
                      <th className="text-left px-2 py-1 border-b border-amber-300">Data</th>
                      <th className="text-left px-2 py-1 border-b border-amber-300">Status</th>
                      <th className="text-left px-2 py-1 border-b border-amber-300">Cosa scrivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appends.map((u) => (
                      <tr key={u.card_id} className="border-b border-amber-200/50">
                        <td className="px-2 py-1 font-semibold">{u.artist_name || "—"}</td>
                        <td className="px-2 py-1">{u.date || "—"}</td>
                        <td className="px-2 py-1">{u.status || "—"}</td>
                        <td className="px-2 py-1 text-[10px] text-[#0A0A0A]/60">
                          {Object.entries(u.fields_to_write || {}).map(([k, v]) => `${k}=${String(v).slice(0, 40)}`).join(" · ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2 border-t-2 border-[#0A0A0A]">
            <button
              onClick={() => onConfirm(true)}
              disabled={busy || updates_count === 0}
              className="px-3 py-2 bg-emerald-600 text-white text-xs uppercase tracking-widest font-bold hover:bg-emerald-700 disabled:opacity-50"
              data-testid="sync-confirm-safe"
            >
              Scrivi SOLO aggiornamenti ({updates_count}) — niente righe nuove
            </button>
            {appends_count > 0 && (
              <button
                onClick={() => {
                  if (window.confirm(`ATTENZIONE: creerò ${appends_count} NUOVE righe nel foglio. Confermi?`)) onConfirm(false);
                }}
                disabled={busy}
                className="px-3 py-2 bg-amber-600 text-white text-xs uppercase tracking-widest font-bold hover:bg-amber-700 disabled:opacity-50"
                data-testid="sync-confirm-full"
              >
                Scrivi tutto (+{appends_count} nuove righe)
              </button>
            )}
            <button
              onClick={onClose}
              disabled={busy}
              className="px-3 py-2 border-2 border-[#0A0A0A] text-xs uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50 ml-auto"
            >
              Annulla (non scrivo nulla)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

